import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';

import { UseOutletContextAsset } from './Asset';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { AssetDA } from '../../../../services/admin/asset.service';
import { Asset, AssetStatus } from '../../../../models/asset.model';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { sortColumn } from '../../../../scripts/table.script';
import { formatMoney } from '../../../../scripts/format.value.script';

import AppPagination from '../../../../components/element/Pagination';
import AppPreloader from '../../../../components/Preloader';
import AppSortTable from '../../../../components/element/SortTable';

export interface AssetListPageProps {};

let errorResponse: Error;

const AssetListPage: React.FunctionComponent<AssetListPageProps> = props => {
  const {setRoute, companyForUser, categoriesForCompany, storageCentersForCompany} = UseOutletContextAsset()
  const {lang} = StateLanguage()

  const [mounted, setMounted] = useState(false)
  const [filter, setFilter] = useState({search: '', status: '' as string, id_category: '', id_storage_center: '', options: {sort_field: '', sort_mode: ''}})
  const [pagination, setPagination] = useState({current: 1, total: 0, pages: 1, limit: 20})
  const [assets, setAssets] = useState<Asset[] | null>(null)

  const loadAssets = async (id_company: string, page: number = 1, limit: number = pagination.limit) => {
    try {
      const response = await AssetDA.getAssets(id_company, filter.search, filter.options.sort_field, filter.options.sort_mode, page, limit, filter.status, filter.id_category, filter.id_storage_center)
      if (response.status === 200) {
        const data = response.data
        const items = data?.items ?? data?.assets ?? (Array.isArray(data) ? data : [])
        const pg = data?.pagination ?? { page, limit, total: items.length, pages: 1 }

        setAssets(items)
        setPagination({current: pg.page ?? page, total: pg.total ?? items.length, pages: pg.pages ?? 1, limit: pg.limit ?? limit})
      } else {
        errorResponse = response.data
        setAssets([])
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      setAssets([])
      errorWebHandler(lang, error)
    }
  }

  const handleChangePage = (page: number, newLimit?: number) => {
    if (companyForUser) loadAssets(companyForUser.id, page, newLimit ?? pagination.limit)
  }

  const handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (companyForUser) {
      filter.search = event.target.value
      filter.options.sort_field = ''
      filter.options.sort_mode = ''
      setFilter({...filter})
      loadAssets(companyForUser.id)
    }
  }

  const handleFilterStatus = (status: string) => {
    if (companyForUser) {
      filter.status = status
      setFilter({...filter})
      loadAssets(companyForUser.id)
    }
  }

  const handleFilterCategory = (id_category: string) => {
    if (companyForUser) {
      filter.id_category = id_category
      setFilter({...filter})
      loadAssets(companyForUser.id)
    }
  }

  const handleFilterStorageCenter = (id_storage_center: string) => {
    if (companyForUser) {
      filter.id_storage_center = id_storage_center
      setFilter({...filter})
      loadAssets(companyForUser.id)
    }
  }

  const executeSortList = (item: string) => {
    if (companyForUser) {
      sortColumn(item, filter.options)
      setFilter({...filter, options: {...filter.options}})
      loadAssets(companyForUser.id)
    }
  }

  const submitAssetDelete = async (item: Asset) => {
    if (!companyForUser) return

    GlobalSwal.fire({
      title: lang.labels.youWantToDeleteTheAsset,
      text: lang.labels.youWillNotBeAbleToUndo,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await AssetDA.deleteAsset(companyForUser.id, item.id)
          if (response.status === 200) {
            GlobalSwal.fire({
              title: lang.labels.assetDeletedSuccess,
              icon: 'success',
              showConfirmButton: false,
              timer: 1500
            })
            loadAssets(companyForUser.id)
          } else {
            errorResponse = response.data
            GlobalSwal.fire({
              title: getError(errorResponse?.code, lang.code, errorResponse?.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        } catch (error) {
          errorWebHandler(lang, error)
        }
      }
    })
  }

  const statusBadge = (status: AssetStatus): {label: string, variant: string} => {
    switch (status) {
      case 'available': return {label: lang.labels.assetStatusAvailable, variant: 'success'}
      case 'rented': return {label: lang.labels.assetStatusRented, variant: 'warning'}
      case 'out_of_service': return {label: lang.labels.assetStatusOutOfService, variant: 'danger'}
      default: return {label: status, variant: 'secondary'}
    }
  }

  const categoryName = (id: string): string => {
    const cat = categoriesForCompany?.find(c => c.id === id)
    return cat?.display_name ?? cat?.name ?? '-'
  }

  const storageCenterName = (id: string): string => {
    const sc = storageCentersForCompany?.find(s => s.id === id)
    return sc?.name ?? '-'
  }

  useEffect(() => {
    if (companyForUser) {
      setFilter({search: '', status: '', id_category: '', id_storage_center: '', options: {sort_field: '', sort_mode: ''}})
      setAssets(null)
      loadAssets(companyForUser.id)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.assets, branch: lang.labels.listAssets}, company: true})
    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { assets
        ?
        <div className="card card-flush">
          <div className="card-header">
            <div className="card-title">
              <div className="d-flex align-items-center position-relative">
                <span className="svg-icon svg-icon-1 position-absolute ms-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="black" />
                    <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="black" />
                  </svg>
                </span>
                <input className="form-control w-350px ps-14" type="text" autoComplete="off" placeholder={lang.labels.searchAsset} value={filter.search} onChange={handleChangeSearch} />
              </div>
            </div>
            <div className="card-toolbar">
              <div className="d-flex justify-content-end align-items-center">
                <Dropdown className="me-2">
                  <Dropdown.Toggle variant="btn btn-sm btn-light btn-active-light-primary">
                    {filter.status ? statusBadge(filter.status as AssetStatus).label : lang.labels.status}
                  </Dropdown.Toggle>
                  <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary w-auto p-3 fs-7" align="end">
                    <Dropdown.Item bsPrefix="menu-item" onClick={() => handleFilterStatus('')}>
                      <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.all}</p>
                    </Dropdown.Item>
                    <Dropdown.Item bsPrefix="menu-item" onClick={() => handleFilterStatus('available')}>
                      <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.assetStatusAvailable}</p>
                    </Dropdown.Item>
                    <Dropdown.Item bsPrefix="menu-item" onClick={() => handleFilterStatus('rented')}>
                      <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.assetStatusRented}</p>
                    </Dropdown.Item>
                    <Dropdown.Item bsPrefix="menu-item" onClick={() => handleFilterStatus('out_of_service')}>
                      <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.assetStatusOutOfService}</p>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                { categoriesForCompany && categoriesForCompany.length > 0 &&
                  <Dropdown className="me-2">
                    <Dropdown.Toggle variant="btn btn-sm btn-light btn-active-light-primary">
                      {filter.id_category ? categoryName(filter.id_category) : lang.labels.category}
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary w-auto p-3 fs-7" align="end">
                      <Dropdown.Item bsPrefix="menu-item" onClick={() => handleFilterCategory('')}>
                        <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.all}</p>
                      </Dropdown.Item>
                      { categoriesForCompany.map((item, index) => (
                        <Dropdown.Item key={index} bsPrefix="menu-item" onClick={() => handleFilterCategory(item.id)}>
                          <p className="menu-link fw-bold px-3 py-1 m-0">{item.display_name ?? item.name}</p>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                }
                { storageCentersForCompany && storageCentersForCompany.length > 0 &&
                  <Dropdown className="me-2">
                    <Dropdown.Toggle variant="btn btn-sm btn-light btn-active-light-primary">
                      {filter.id_storage_center ? storageCenterName(filter.id_storage_center) : lang.labels.storageCenter}
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary w-auto p-3 fs-7" align="end">
                      <Dropdown.Item bsPrefix="menu-item" onClick={() => handleFilterStorageCenter('')}>
                        <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.all}</p>
                      </Dropdown.Item>
                      { storageCentersForCompany.map((item, index) => (
                        <Dropdown.Item key={index} bsPrefix="menu-item" onClick={() => handleFilterStorageCenter(item.id)}>
                          <p className="menu-link fw-bold px-3 py-1 m-0">{item.name}</p>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                }
                <Link to="/main/inventory/asset/create" className="btn btn-primary">
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                        <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                        <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                      </svg>
                    </span>
                    {lang.labels.createAsset}
                  </div>
                </Link>
              </div>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("name")}>
                          <span className="me-1">{lang.labels.name}</span>
                          <AppSortTable attribute={"name"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-100px">{lang.labels.assetCode}</th>
                      <th className="min-w-200px">{lang.labels.description || 'Description'}</th>
                      <th className="w-125px">{lang.labels.category}</th>
                      <th className="w-125px">{lang.labels.storageCenter}</th>
                      <th className="w-100px">{lang.labels.status}</th>
                      <th className="w-150px">{lang.labels.currentTenant}</th>
                      <th className="w-100px text-end">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  { assets.length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { assets.map((item, index) => {
                        const badge = statusBadge(item.status)
                        return (
                          <tr key={index} className="row-dashed">
                            <td>
                              <Link to={"/main/inventory/asset/view/" + item.id} className="fw-bolder text-dark text-hover-primary">{item.name}</Link>
                            </td>
                            <td>{item.code || '-'}</td>
                            <td className="text-gray-700">
                              <span className="d-inline-block text-truncate" style={{ maxWidth: '250px' }} title={item.description || ''}>
                                {item.description || '-'}
                              </span>
                            </td>
                            <td>{categoryName(item.id_category)}</td>
                            <td>{storageCenterName(item.id_storage_center)}</td>
                            <td>
                              <span className={`badge badge-light-${badge.variant}`}>{badge.label}</span>
                            </td>
                            <td>{item.current_tenant?.name ?? '-'}</td>
                            <td className="text-end">
                              <Dropdown>
                                <Dropdown.Toggle variant="btn btn-sm btn-light btn-active-light-primary">
                                  <div className="d-flex align-items-center">
                                    {lang.labels.actions}
                                    <span className="svg-icon svg-icon-5 me-0">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black"></path>
                                      </svg>
                                    </span>
                                  </div>
                                </Dropdown.Toggle>
                                <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }} variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary w-auto p-3 fs-7" align="end">
                                  <Dropdown.Item as={"div"} bsPrefix="menu-item">
                                    <Link to={"/main/inventory/asset/view/" + item.id} className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.view}</Link>
                                  </Dropdown.Item>
                                  <Dropdown.Item as={"div"} bsPrefix="menu-item">
                                    <Link to={"/main/inventory/asset/update/" + item.id} className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.edit}</Link>
                                  </Dropdown.Item>
                                  { item.status !== 'rented' &&
                                    <Dropdown.Item bsPrefix="menu-item" onClick={() => submitAssetDelete(item)}>
                                      <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.delete}</p>
                                    </Dropdown.Item>
                                  }
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={8} className="text-center">{lang.labels.noAssetsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              <AppPagination
                showOption={true}
                pagination={{
                  current: pagination.current,
                  total: pagination.total,
                  limit: pagination.limit,
                  pages: Array.from({ length: pagination.pages }, (_, i) => i + 1),
                }}
                loadPagination={handleChangePage}
              ></AppPagination>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default AssetListPage;
